/* eslint-disable import/prefer-default-export */

/**
 *
 * Take care to ensure the get and update queries for each type
 * return the same fields as one another.
 *
 */

export const getEmployer = /* GraphQL */ `
  query ContainerGetEmployer($id: ID!) {
    getEmployer(id: $id) {
      id
      slug
      email
      username
      type
      userAgreementAcknowledged
      newsletterModalViewCount
      personalEmail
      personalPhone
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      lastname
      middlename
      mostRecentlyInvited
      lowerFull
      address
      phone
      title
      companyIDs
      archived
      loginStatus {
        isEnabled
        status
      }
      companies(limit: 200) {
        items {
          id
          company {
            id
            name
            displayCompanyName
            slug
          }
        }
      }
      contributorCases(stateCaseID: { beginsWith: { state: OPEN } }) {
        items {
          case {
            id
            slug
            foreignNational {
              firstname
              lastname
            }
            phaseRequirements {
              phase
              milestones
            }
            completedMilestones {
              completionDate
              completedByID
              milestone
              receiptNumber
            }
            startDate
            state
          }
        }
      }
    }
  }
`;

export const updateEmployer = /* GraphQL */ `
  mutation ContainerUpdateEmployer(
    $input: UpdateEmployerInput!
    $condition: ModelEmployerConditionInput
  ) {
    updateEmployer(input: $input, condition: $condition) {
      id
      slug
      email
      username
      type
      personalEmail
      personalPhone
      newsletterModalViewCount
      userAgreementAcknowledged
      hasCompletedOnboardingQuestions
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      lastname
      middlename
      lowerFull
      address
      phone
      title
      companyIDs
      archived
      loginStatus {
        isEnabled
        status
      }
      companies(limit: 200) {
        items {
          id
          company {
            id
            name
            displayCompanyName
            slug
          }
        }
      }
    }
  }
`;

export const getForeignNational = /* GraphQL */ `
  query ContainerGetForeignNational($id: ID!) {
    getForeignNational(id: $id) {
      __typename
      type
      id
      username
      slug
      employeeId
      userAgreementAcknowledged
      email
      legalTeamIDs
      emailPersonal
      emailWork
      createdBy
      fileRequirementsManipulated
      onboardingSections {
        background
        contact
        employment
        family
        immigration
        personal
      }
      completedOnboardingQuestionsBy
      usMailingAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      usResidentialAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      lastAddressOutsideUS {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      username
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      middlename
      lastname
      notes
      nickname
      additionalFirstnames
      additionalMiddlenames
      additionalLastnames
      lowerFirst
      lowerLast
      lowerFull
      maritalStatus
      spouse
      dependents
      phone
      phoneWork
      companyID
      mostRecentlyInvited
      archived
      chargeabilityCountry
      citizenshipEligibilityDate
      socCode
      firstEntryUS
      taxTrackingF1J1
      startDate
      workLocation {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      relatives
      relativeAdmins
      relativesAsSource {
        items {
          target {
            id
            relativeAdmins
          }
        }
      }
      relativesAsTarget {
        items {
          source {
            id
            relativeAdmins
          }
        }
      }
      immigrationStatus {
        status
        doesNotExpire
        expiration
      }
      nonImmigrantVisaNumber
      visaIssueDate
      visaExpiration
      visaExpDate
      visaConsulate
      immigrantVisaApplicant
      immigrantPetitionEverFiled
      j1j2 {
        hasHad
        start
        end
      }
      hasHadI485Filed {
        value
        followPrompt
        followValue
      }
      hasPendingETA9089
      hasApprovedI140 {
        hasApproved
        classification
        priorityDate
        employmentBasedCategory
        petitionerI140
      }
      hasValidOrExpiredEAD
      hasPendingUSCISApplication
      hasHadVisaPetitionRevokedOrDenied
      hasBeenArrestedSinceLastUSEntry
      criminalHistory {
        tortureGenocide
        killing
        severeInjuring
        nonconsensualSex
        religiousBelief
      }
      hasTransportedWeapons
      hasParticipatedInArmedGroup
      hasDetailedPerson
      hasUsedWeapons
      hasWeaponsTraining
      hasCriminalOffence
      hasViolatedVisaTerms
      isInRemovalProceedings {
        value
        reason
      }
      hasBeenEmployedSinceLastAdmitted {
        value
        reason
      }
      citizenshipCountry
      birthCountry
      birthState
      birthCity
      birthDate
      jobDescriptionID
      offeredSalary {
        currency
        amount
        period
      }
      isRemote
      loginStatus {
        isEnabled
        status
      }
      hasNonUSPassport
      aosFilingEligible
      nivMaxout
      passportGender
      passportIssueDate
      passportExpirationDate
      passportNumber
      passportCountry
      usSSN
      hasSSN
      aNumber
      lastEntryUS
      i94ArrivalDepartureRecordNumber
      sevisNumber
      eadNumber
      createdAt
      updatedAt
      relativesAsSource {
        nextToken
      }
      relativesAsTarget {
        nextToken
      }
      company {
        id
        name
        displayCompanyName
        lowerName
        lowerNameKey
        slug
        type
        domainWhitelist
        isPartnerCompany
        isParent
        parentID
        archived
        accountManagerID
        assignedAttorneys {
          items {
            attorney {
              id
            }
          }
        }
        assignedParalegals {
          items {
            paralegal {
              id
            }
          }
        }
        assignedEmployers {
          items {
            employer {
              id
            }
          }
        }
        logo {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
        }
      }
      jobDescription {
        companyID
        id
        archived
        type
        foreignNationalID
        title
        officialTitle
        degrees
        requiresBachelors
        requiresMasters
        requiresPhd
        dotNumber
        caseSocCode
        duties
        jobURL
        majors
        specialRequirements
        yearsGeneralExperience
        objectiveSkillSetsRequired
        offeredSalary
        certificationLicenses
        travelIsRequired
        travelPercentage
        travelDomestic
        travelInternational
        supervisesOthers
        supervisesOthersCount
        supervisesTitles
        supervisorTitle
        canWorkFromHome
        uploadedDescriptionID
        createdAt
        updatedAt
      }
      cases {
        items {
          id
          slug
          startDate
          requestedProcess
          requestedProcessSubCategory
          requestedProcessAdditionalProcessing
          globalRequestedProcessSubCategory
          globalProcessCountryCode
          caseNumber
          files {
            items {
              id
              fileID
              caseID
              file {
                id
                fileType
              }
            }
          }
          fileRequirements {
            type
            assignedToID
            isAssignedToLegalAssistants
            expirationDateRequired
            issueDateRequired
            isApproved
            isRequested
            hidden
            durationOfStatus
          }
          state
          currentPhase
          company {
            name
            displayCompanyName
          }
          phaseRequirements {
            phase
            milestones
          }
          completedMilestones {
            completionDate
            completedByID
            milestone
            receiptNumber
          }
          contributors {
            items {
              id
              contributorID
              contributorEmployer {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorForeignNational {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorTeamMember {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
            }
          }
          sponsoringEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          attorney {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          caseManager {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
        }
      }
      sponsoredCases {
        nextToken
      }
      fileRequirements {
        type
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
        hidden
        durationOfStatus
      }
      files {
        items {
          id
          expirationDate
          durationOfStatus
          issueDate
          dueDate
          fileName
          fileType
          contentType
          original {
            bucket
            region
            key
            level
            identityId
          }
          processRequestID
          companyID
          sourceID
          ownerNationalID
          uploadDate
          uploaderID
          uploaderEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderForeignNational {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderTeamMember {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const updateForeignNational = /* GraphQL */ `
  mutation ContainerUpdateForeignNational(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      __typename
      type
      id
      username
      immigrationVisaStatus
      slug
      employeeId
      userAgreementAcknowledged
      email
      emailPersonal
      emailWork
      createdBy
      fileRequirementsManipulated
      citizenshipEligibilityDate
      onboardingSections {
        background
        contact
        employment
        family
        immigration
        personal
      }
      completedOnboardingQuestionsBy
      usMailingAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      usResidentialAddress {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      lastAddressOutsideUS {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      username
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      middlename
      lastname
      notes
      nickname
      additionalFirstnames
      additionalMiddlenames
      additionalLastnames
      lowerFirst
      lowerLast
      lowerFull
      maritalStatus
      spouse
      dependents
      phone
      phoneWork
      companyID
      mostRecentlyInvited
      archived
      chargeabilityCountry
      socCode
      firstEntryUS
      taxTrackingF1J1
      startDate
      workLocation {
        careOf
        address
        companyAddressType
        unitNumber
        unitType
        adminArea
        state
        city
        country
        postalCode
        title
      }
      relatives
      relativeAdmins
      relativesAsSource {
        items {
          target {
            id
            relativeAdmins
          }
        }
      }
      relativesAsTarget {
        items {
          source {
            id
            relativeAdmins
          }
        }
      }
      immigrationStatus {
        status
        doesNotExpire
        expiration
      }
      nonImmigrantVisaNumber
      visaIssueDate
      visaExpiration
      visaExpDate
      visaConsulate
      immigrantVisaApplicant
      immigrantPetitionEverFiled
      j1j2 {
        hasHad
        start
        end
      }
      hasHadI485Filed {
        value
        followPrompt
        followValue
      }
      hasPendingETA9089
      hasApprovedI140 {
        hasApproved
        classification
        priorityDate
        employmentBasedCategory
        petitionerI140
      }
      hasValidOrExpiredEAD
      hasPendingUSCISApplication
      hasHadVisaPetitionRevokedOrDenied
      hasBeenArrestedSinceLastUSEntry
      criminalHistory {
        tortureGenocide
        killing
        severeInjuring
        nonconsensualSex
        religiousBelief
      }
      hasTransportedWeapons
      hasParticipatedInArmedGroup
      hasDetailedPerson
      hasUsedWeapons
      hasWeaponsTraining
      hasCriminalOffence
      hasViolatedVisaTerms
      isInRemovalProceedings {
        value
        reason
      }
      hasBeenEmployedSinceLastAdmitted {
        value
        reason
      }
      citizenshipCountry
      birthCountry
      birthState
      birthCity
      birthDate
      jobDescriptionID
      offeredSalary {
        currency
        amount
        period
      }
      isRemote
      loginStatus {
        isEnabled
        status
      }
      hasNonUSPassport
      aosFilingEligible
      nivMaxout
      passportGender
      passportIssueDate
      passportExpirationDate
      passportNumber
      passportCountry
      usSSN
      hasSSN
      aNumber
      lastEntryUS
      i94ArrivalDepartureRecordNumber
      sevisNumber
      eadNumber
      createdAt
      updatedAt
      relativesAsSource {
        nextToken
      }
      relativesAsTarget {
        nextToken
      }
      company {
        id
        name
        displayCompanyName
        lowerName
        lowerNameKey
        slug
        type
        domainWhitelist
        isPartnerCompany
        isParent
        parentID
        archived
        accountManagerID
        assignedAttorneys {
          items {
            attorney {
              id
            }
          }
        }
        assignedParalegals {
          items {
            paralegal {
              id
            }
          }
        }
        assignedEmployers {
          items {
            employer {
              id
            }
          }
        }
        logo {
          original {
            bucket
            identityId
            key
            level
            region
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              identityId
              key
              level
              region
            }
            size
          }
        }
      }
      jobDescription {
        companyID
        id
        archived
        type
        foreignNationalID
        title
        officialTitle
        degrees
        requiresBachelors
        requiresMasters
        requiresPhd
        dotNumber
        caseSocCode
        duties
        jobURL
        majors
        specialRequirements
        yearsGeneralExperience
        objectiveSkillSetsRequired
        offeredSalary
        certificationLicenses
        travelIsRequired
        travelPercentage
        travelDomestic
        travelInternational
        supervisesOthers
        supervisesOthersCount
        supervisesTitles
        supervisorTitle
        canWorkFromHome
        uploadedDescriptionID
        createdAt
        updatedAt
      }
      processRequests(filter: { state: { eq: REQUESTED } }) {
        items {
          id
          requestedProcess
          slug
          requestedProcessSubCategory
          requestedProcessAdditionalProcessing
          globalRequestedProcessSubCategory
          globalProcessCountryCode
          requestDate
          state
          initiator {
            id
            slug
            firstname
            lastname
          }
          requester {
            id
            slug
            firstname
            lastname
          }
        }
      }
      cases {
        items {
          id
          slug
          startDate
          requestedProcess
          requestedProcessSubCategory
          requestedProcessAdditionalProcessing
          globalRequestedProcessSubCategory
          globalProcessCountryCode
          caseNumber
          files {
            items {
              id
              file {
                id
                fileType
              }
            }
          }
          fileRequirements {
            type
            assignedToID
            isAssignedToLegalAssistants
            expirationDateRequired
            issueDateRequired
            isApproved
            isRequested
            hidden
            durationOfStatus
          }
          state
          currentPhase
          company {
            name
            displayCompanyName
          }
          phaseRequirements {
            phase
            milestones
          }
          completedMilestones {
            completionDate
            completedByID
            milestone
            receiptNumber
          }
          contributors {
            items {
              id
              contributorID
              contributorEmployer {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorForeignNational {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              contributorTeamMember {
                id
                type
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
            }
          }
          sponsoringEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          attorney {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          caseManager {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
        }
      }
      sponsoredCases {
        nextToken
      }
      fileRequirements {
        type
        assignedToID
        expirationDateRequired
        issueDateRequired
        isApproved
        hidden
        durationOfStatus
      }
      files {
        items {
          id
          expirationDate
          durationOfStatus
          issueDate
          fileName
          fileType
          contentType
          original {
            bucket
            region
            key
            level
            identityId
          }
          processRequestID
          companyID
          sourceID
          ownerNationalID
          uploadDate
          uploaderID
          uploaderEmployer {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderForeignNational {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          uploaderTeamMember {
            id
            firstname
            lastname
            slug
            avatar {
              original {
                bucket
                key
                level
                region
                identityId
              }
              square {
                bucket
                key
                level
                region
                identityId
              }
              squareThumbnails {
                image {
                  bucket
                  key
                  level
                  region
                  identityId
                }
                size
              }
              version
            }
          }
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const customGetForeignNationalFiles = /* GraphQL */ `
  query customGetForeignNationalFiles($id: ID!) {
    getForeignNational(id: $id) {
      id
      eadExpDate
      I797ExpDate
      spouse
      files {
        items {
          id
          expirationDate
          issueDate
          fileType
          issueDate
          dueDate
          durationOfStatus
        }
      }
    }
  }
`;

export const updateForeignNationalFiles = /* GraphQL */ `
  mutation updateForeignNationalFiles(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      __typename
      id
      eadExpDate
      I797ExpDate
      citizenshipEligibilityDate
      spouse
      files {
        items {
          id
          expirationDate
          issueDate
          fileType
          issueDate
          dueDate
          durationOfStatus
        }
      }
    }
  }
`;

export const getForeignNationalNotes = /* GraphQL */ `
  query ContainerGetForeignNationalNotes($id: ID!) {
    getForeignNational(id: $id) {
      __typename
      type
      id
      notes
    }
  }
`;

export const updateForeignNationalNotes = /* GraphQL */ `
  mutation ContainerUpdateForeignNationalNotes(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      __typename
      type
      id
      notes
    }
  }
`;

export const getCustomTeamMember = /* GraphQL */ `
  query GetCustomTeamMember($id: ID!) {
    getTeamMember(id: $id) {
      id
      slug
      firstname
      lastname
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
    }
  }
`;

export const getTeamMember = /* GraphQL */ `
  query ContainerGetTeamMember($id: ID!) {
    getTeamMember(id: $id) {
      id
      slug
      type
      email
      username
      userAgreementAcknowledged
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      middlename
      lastname
      lowerFirst
      lowerLast
      lowerFull
      phone
      position
      title
      licensingAuthority
      stateBarNumber
      isAdmin
      managerID
      mostRecentlyInvited
      archived
      loginStatus {
        isEnabled
        status
      }
      createdAt
      updatedAt
      manager {
        id
        slug
        type
        email
        username
        firstname
        middlename
        lastname
        lowerFirst
        lowerLast
        lowerFull
        phone
        position
        title
        licensingAuthority
        stateBarNumber
        isAdmin
        managerID
        mostRecentlyInvited
        archived
        createdAt
        updatedAt
      }
    }
  }
`;

export const updateTeamMember = /* GraphQL */ `
  mutation ContainerUpdateTeamMember(
    $input: UpdateTeamMemberInput!
    $condition: ModelTeamMemberConditionInput
  ) {
    updateTeamMember(input: $input, condition: $condition) {
      id
      slug
      type
      email
      username
      userAgreementAcknowledged
      avatar {
        original {
          bucket
          key
          level
          region
          identityId
        }
        square {
          bucket
          key
          level
          region
          identityId
        }
        squareThumbnails {
          image {
            bucket
            key
            level
            region
            identityId
          }
          size
        }
        version
      }
      firstname
      middlename
      lastname
      lowerFirst
      lowerLast
      lowerFull
      phone
      position
      title
      licensingAuthority
      stateBarNumber
      isAdmin
      managerID
      mostRecentlyInvited
      archived
      loginStatus {
        isEnabled
        status
      }
      createdAt
      updatedAt
      manager {
        id
        slug
        type
        email
        username
        firstname
        middlename
        lastname
        lowerFirst
        lowerLast
        lowerFull
        phone
        position
        title
        licensingAuthority
        stateBarNumber
        isAdmin
        managerID
        mostRecentlyInvited
        archived
        createdAt
        updatedAt
      }
    }
  }
`;

export const customSearchForeignNationals = /* GraphQL */ `
  query CustomSearchForeignNationals(
    $filter: SearchableForeignNationalFilterInput
    $limit: Int
    $nextToken: String
    $sort: [SearchableForeignNationalSortInput]
  ) {
    searchForeignNationals(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
      sort: $sort
    ) {
      items {
        type
        id
        immigrationCurrentStatus
        immigrationStatusExpiration
        employmentBasedCategory
        priorityDate
        birthCountryCode
        eadExpDate
        I797ExpDate
        visaExpDate
        username
        slug
        companyName
        employeeId
        userAgreementAcknowledged
        email
        emailPersonal
        emailWork
        createdBy
        onboardingSections {
          background
          contact
          employment
          family
          immigration
          personal
        }
        completedOnboardingQuestionsBy
        usMailingAddress {
          careOf
          address
          companyAddressType
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
        }
        usResidentialAddress {
          careOf
          address
          companyAddressType
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
        }
        lastAddressOutsideUS {
          careOf
          address
          companyAddressType
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
        }
        username
        avatar {
          original {
            bucket
            key
            level
            region
            identityId
          }
          square {
            bucket
            key
            level
            region
            identityId
          }
          squareThumbnails {
            image {
              bucket
              key
              level
              region
              identityId
            }
            size
          }
          version
        }
        firstname
        middlename
        lastname
        nickname
        additionalFirstnames
        additionalMiddlenames
        additionalLastnames
        lowerFirst
        lowerLast
        lowerFull
        maritalStatus
        spouse
        dependents
        phone
        phoneWork
        companyID
        mostRecentlyInvited
        archived
        workLocation {
          careOf
          address
          companyAddressType
          unitNumber
          unitType
          adminArea
          state
          city
          country
          postalCode
          title
        }
        relatives
        relativeAdmins
        relativesAsSource {
          items {
            target {
              id
              relativeAdmins
            }
          }
        }
        relativesAsTarget {
          items {
            source {
              id
              relativeAdmins
            }
          }
        }
        immigrationStatus {
          status
          doesNotExpire
          expiration
        }
        immigrationVisaStatus
        nonImmigrantVisaNumber
        visaIssueDate
        visaExpiration
        visaConsulate
        immigrantVisaApplicant
        immigrantPetitionEverFiled
        j1j2 {
          hasHad
          start
          end
        }
        hasHadI485Filed {
          value
          followPrompt
          followValue
        }
        hasPendingETA9089
        hasApprovedI140 {
          hasApproved
          classification
          priorityDate
        }
        hasValidOrExpiredEAD
        hasPendingUSCISApplication
        hasHadVisaPetitionRevokedOrDenied
        hasBeenArrestedSinceLastUSEntry
        criminalHistory {
          tortureGenocide
          killing
          severeInjuring
          nonconsensualSex
          religiousBelief
        }
        hasTransportedWeapons
        hasParticipatedInArmedGroup
        hasDetailedPerson
        hasUsedWeapons
        hasWeaponsTraining
        hasCriminalOffence
        hasViolatedVisaTerms
        isInRemovalProceedings {
          value
          reason
        }
        hasBeenEmployedSinceLastAdmitted {
          value
          reason
        }
        citizenshipCountry
        birthCountry
        birthState
        birthCity
        birthDate
        jobDescriptionID
        offeredSalary {
          currency
          amount
          period
        }
        isRemote
        loginStatus {
          isEnabled
          status
        }
        hasNonUSPassport
        aosFilingEligible
        nivMaxout
        passportGender
        passportIssueDate
        passportExpirationDate
        passportNumber
        passportCountry
        usSSN
        hasSSN
        aNumber
        lastEntryUS
        i94ArrivalDepartureRecordNumber
        sevisNumber
        eadNumber
        createdAt
        updatedAt
        relativesAsSource {
          nextToken
        }
        relativesAsTarget {
          nextToken
        }
        company {
          id
          name
          displayCompanyName
          lowerName
          lowerNameKey
          slug
          type
          domainWhitelist
          isPartnerCompany
          isParent
          parentID
          archived
          accountManagerID
          assignedAttorneys {
            items {
              attorney {
                id
              }
            }
          }
          assignedParalegals {
            items {
              paralegal {
                id
              }
            }
          }
          assignedEmployers {
            items {
              employer {
                id
              }
            }
          }
          logo {
            original {
              bucket
              identityId
              key
              level
              region
            }
            square {
              bucket
              key
              level
              region
              identityId
            }
            squareThumbnails {
              image {
                bucket
                identityId
                key
                level
                region
              }
              size
            }
          }
          fileRequirements {
            type
            assignedToID
            expirationDateRequired
            issueDateRequired
            isApproved
            durationOfStatus
          }
          files {
            items {
              id
              expirationDate
              issueDate
              fileName
              fileType
              contentType
              original {
                bucket
                region
                key
                level
                identityId
              }
              processRequestID
              companyID
              sourceID
              ownerNationalID
              uploadDate
              uploaderID
              uploaderEmployer {
                id
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              uploaderForeignNational {
                id
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              uploaderTeamMember {
                id
                firstname
                lastname
                slug
                avatar {
                  original {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  square {
                    bucket
                    key
                    level
                    region
                    identityId
                  }
                  squareThumbnails {
                    image {
                      bucket
                      key
                      level
                      region
                      identityId
                    }
                    size
                  }
                  version
                }
              }
              createdAt
              updatedAt
            }
          }
        }

        files {
          items {
            id
            expirationDate
            issueDate
            fileType
            issueDate
            dueDate
          }
        }
      }
      nextToken
    }
  }
`;

export const CustomUpdateForeignNational = /* GraphQL */ `
  mutation CustomUpdateForeignNational(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      __typename
      type
      id
      username
      firstname
      lastname
      immigrationVisaStatus
      slug
      employeeId
      archived
      immigrationStatus {
        status
        doesNotExpire
        expiration
      }
    }
  }
`;

export const CustomUpdateForeignNationalfilterFields = /* GraphQL */ `
  mutation CustomUpdateForeignNationalfilterFields(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      id
      firstname
      lastname
      visaExpDate
      companyName
    }
  }
`;

export const CustomUpdateForeignNationalAgreement = /* GraphQL */ `
  mutation CustomUpdateForeignNationalAgreement(
    $input: UpdateForeignNationalInput!
    $condition: ModelForeignNationalConditionInput
  ) {
    updateForeignNational(input: $input, condition: $condition) {
      id
      userAgreementAcknowledged,
      userAgreementAcknowledgementDate,
      userAgreementAcknowledgementText
    }
  }
`;
